import { ArrowLeft, LayoutDashboard, Loader2, Rocket, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SortDropdown } from "@/components/shared";
import { featureFlags } from "@/config/featureFlags";
import { microAppService } from "@/lib/services";
import type { MicroAppSummary } from "@/types";
import { MicroAppCard } from "./microApps/MicroAppCard";

type OwnershipFilter = "all" | "mine" | "shared";
type SortKey = "newest" | "recently_updated" | "alphabetical";

const FILTER_LABELS: Record<OwnershipFilter, string> = {
  all: "All",
  mine: "Mine",
  shared: "Shared with me",
};

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="h-14 w-14 rounded-2xl bg-brand-primary/10 flex items-center justify-center mb-4">
        <LayoutDashboard className="h-7 w-7 text-brand-primary" />
      </div>
      <h3 className="text-lg font-semibold text-content-primary">No micro apps yet</h3>
      <p className="text-sm text-content-tertiary mt-1 max-w-md">
        There’s no separate builder — micro apps are made from dashboards you generate in chat.
      </p>
      <ol className="text-sm text-content-tertiary mt-4 max-w-md text-left space-y-1.5 list-decimal list-inside">
        <li>Ask a chat to generate a dashboard or chart.</li>
        <li>Open the generated dashboard.</li>
        <li>
          Click{" "}
          <span className="inline-flex items-center gap-1 text-content-secondary font-medium">
            <Rocket className="h-3.5 w-3.5" aria-hidden /> Publish as app
          </span>{" "}
          to publish it here.
        </li>
      </ol>
    </div>
  );
}

function Gallery({
  loading,
  apps,
  onOpen,
}: Readonly<{ loading: boolean; apps: MicroAppSummary[]; onOpen: (uuid: string) => void }>) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
      </div>
    );
  }
  if (apps.length === 0) return <EmptyState />;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {apps.map((app) => (
        <MicroAppCard key={app.uuid} app={app} onOpen={onOpen} />
      ))}
    </div>
  );
}

/** Apply the search query, ownership filter, and sort ordering to the app list. */
function selectApps(
  apps: MicroAppSummary[],
  searchQuery: string,
  filter: OwnershipFilter,
  sortBy: SortKey,
): MicroAppSummary[] {
  const q = searchQuery.trim().toLowerCase();
  const result = apps.filter((a) => {
    if (filter === "mine" && a.isShared) return false;
    if (filter === "shared" && !a.isShared) return false;
    if (!q) return true;
    return a.name.toLowerCase().includes(q) || (a.description?.toLowerCase().includes(q) ?? false);
  });
  result.sort((a, b) => {
    if (sortBy === "alphabetical") return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    if (sortBy === "recently_updated") return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
  return result;
}

export function MicroApps() {
  const navigate = useNavigate();
  const [apps, setApps] = useState<MicroAppSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState<OwnershipFilter>("all");
  const [sortBy, setSortBy] = useState<SortKey>("newest");

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const response = await microAppService.list();
        if (active) setApps(response.data);
      } catch (error) {
        console.error("Failed to load micro apps:", error);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => selectApps(apps, searchQuery, filter, sortBy), [apps, searchQuery, filter, sortBy]);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="shrink-0 p-6 border-b border-border-primary">
        <div className="flex items-center gap-3 mb-1">
          {featureFlags.apps && (
            <button
              type="button"
              onClick={() => navigate("/apps")}
              className="p-1.5 rounded-lg hover:bg-interactive-hover text-content-tertiary transition"
              aria-label="Back to apps"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}
          <h1 className="text-xl font-semibold text-content-primary">Micro Apps</h1>
        </div>
        <p className={`text-sm text-content-tertiary ${featureFlags.apps ? "ml-9" : ""}`}>
          Turn a dashboard you generated in chat into a shareable app — open it, then click{" "}
          <span className="inline-flex items-center gap-1 text-content-secondary font-medium">
            <Rocket className="h-3.5 w-3.5" aria-hidden /> Publish as app
          </span>
          {". "}Published apps appear here for you and anyone you share them with.
        </p>

        <div
          className={`mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-4 ${featureFlags.apps ? "ml-9" : ""}`}
        >
          <div className="relative flex-1 max-w-md w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-tertiary" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search micro apps"
              className="w-full pl-9 pr-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-tertiary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 text-sm"
            />
          </div>
          <div className="flex gap-2">
            {(["all", "mine", "shared"] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  filter === f
                    ? "bg-brand-primary-subtle text-brand-primary border border-brand-primary/30"
                    : "bg-surface-primary text-content-tertiary border border-border-primary hover:bg-interactive-hover"
                }`}
              >
                {FILTER_LABELS[f]}
              </button>
            ))}
          </div>
          <div className="ml-auto">
            <SortDropdown
              options={[
                { id: "newest", label: "Newest" },
                { id: "recently_updated", label: "Recently updated" },
                { id: "alphabetical", label: "Alphabetical" },
              ]}
              value={sortBy}
              onChange={setSortBy}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <Gallery loading={loading} apps={filtered} onOpen={(uuid) => navigate(`/apps/view/${uuid}`)} />
      </div>
    </div>
  );
}
